import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Badge,
  Box,
  Button,
  Grid,
  HStack,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import UserProfileTab from "./UserProfileTab";
import { useContext } from "react";
import { UserProfileContext } from "../../../context/UserContext";
import VTUFeatureCard, { VTU_FEATURE_MOCK_DATA } from "./VTUFeatureCard";
import ReusableModal from "../../../components/ReusableModal";
import ReusableTable from "../../../components/ReusableTable";
import PayBillModal from "./PayBillModal";
import { VtuProviderDetails } from "./payBillModal/VtuProviderDetails";
import { getUserVtuTransactions } from "../../vtu/services";
import { verifyVtuPaystackPayment } from "../../vtu/services";
import {
  getStoredPendingPayment,
  clearStoredPendingPayment,
  clearReturnParams,
  getVtuPaymentErrorMessage,
} from "./payBillModal/utils";
import { setStoredPendingPayment } from "./payBillModal/utils";
import {
  errorNotifier,
  successNotifier,
} from "../../../components/NotificationHandler";
import { getSystemHealth } from "../../../services/healthService";

export const VTUFeature = () => {
  const {
    isOpen: isTransactionModalOpen,
    onOpen: openTransactionModal,
    onClose: closeTransactionModal,
  } = useDisclosure();
  const {
    isOpen: isPayBillModalOpen,
    onOpen: openPayBillModal,
    onClose: closePayBillModal,
  } = useDisclosure();
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedPayItem, setSelectedPayItem] = useState("data");

  const { data: userTransactions, isLoading: transactionsLoading } = useQuery({
    queryKey: ["vtu-transactions", { page: 1, limit: 10 }],
    queryFn: () => getUserVtuTransactions({ page: 1, limit: 10 }),
  });

  // VTU purchase tiles must not be presented as usable when the backend's
  // VTU provider isn't configured/live — default to available (fail open)
  // while this is loading or if the health check itself fails, so a
  // transient health-check hiccup doesn't take down a working feature.
  const { data: systemHealth } = useQuery({
    queryKey: ["system-health"],
    queryFn: getSystemHealth,
    staleTime: 60000,
  });
  const vtuAvailable = systemHealth ? systemHealth.vtuAvailable !== false : true;

  const queryClient = useQueryClient();
  const hasAutoVerifiedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isPaystackReturn = params.get("vtuPayReturn") === "1";
    if (!isPaystackReturn) return;

    if (isPayBillModalOpen) return;

    if (hasAutoVerifiedRef.current) return;
    hasAutoVerifiedRef.current = true;

    const pending = getStoredPendingPayment();
    if (!pending?.reference || !pending?.transactionId) {
      errorNotifier("Missing pending payment for verification");
      clearReturnParams();
      return;
    }

    const verifyBackground = async () => {
      try {
        // mark verifying in storage so modal can reflect ongoing verification
        setStoredPendingPayment({ ...pending, verifying: true });
        await verifyVtuPaystackPayment({
          reference: pending.reference,
          transactionId: pending.transactionId,
        });
        clearStoredPendingPayment();
        clearReturnParams();
        queryClient.invalidateQueries({ queryKey: ["vtu-transactions"] });
        successNotifier("VTU payment successful");
      } catch (err) {
        console.error("Background VTU verify error:", err?.response ?? err);
        // clear verifying flag but keep pending so modal can show error state
        setStoredPendingPayment({ ...pending, verifying: false });
        errorNotifier(getVtuPaymentErrorMessage(err));
      }
    };

    verifyBackground();
  }, [isPayBillModalOpen, queryClient]);

  const formattedTransactions = useMemo(() => {
    const transactions = Array.isArray(userTransactions?.items)
      ? userTransactions.items
      : [];

    return transactions.map((transaction) => ({
      ...transaction,
      amount: Number(transaction.amount) || 0,
      date: transaction?.date
        ? new Date(transaction.date).toLocaleString()
        : "-",
    }));
  }, [userTransactions]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isPaystackReturn = params.get("vtuPayReturn") === "1";
    if (!isPaystackReturn) return;

    const returnedItem = params.get("vtuItem");
    if (
      returnedItem &&
      ["data", "electricity", "airtime", "cable"].includes(returnedItem)
    ) {
      setSelectedPayItem(returnedItem);
    }

    openPayBillModal();
  }, [openPayBillModal]);

  const handleView = (transaction) => {
    setSelectedTransaction(transaction);
    openTransactionModal();
  };

  const handleOpenPayBillModal = (itemKey) => {
    setSelectedPayItem(itemKey);
    openPayBillModal();
  };

  const getStatusColor = (status) => {
    if (status === "Successful") return "green";
    if (status === "Pending") return "yellow";
    return "red";
  };

  const columns = [
    {
      key: "itemTitle",
      header: "Title",
    },
    {
      key: "phoneOrDecoderNumber",
      header: "Phone/Decoder Number",
    },
    {
      key: "amount",
      header: "Amount",
      isNumeric: true,
      render: (row) => `₦${row.amount.toLocaleString()}`,
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <Badge
          colorScheme={getStatusColor(row.status)}
          borderRadius="full"
          px="8px"
          py="2px"
          textTransform="none"
        >
          {row.status}
        </Badge>
      ),
    },
    {
      key: "date",
      header: "Date",
    },
    {
      key: "action",
      header: "Action",
      render: (row) => (
        <Button size="xs" onClick={() => handleView(row)}>
          View
        </Button>
      ),
    },
  ];

  const { userProfile } = useContext(UserProfileContext);

  const displayName =
    (userProfile?.firstName || userProfile?.name || "") +
    (userProfile?.lastName ? ` ${userProfile.lastName}` : "");
  const phone =
    userProfile?.phone || userProfile?.mobile || userProfile?.phoneNumber || "";

  return (
    <Box
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="default"
      p={"20px"}
      bg="white"
    >
      <UserProfileTab name={displayName || ""} phone={phone || ""} />

      <Text mt={"2em"} mb={"1em"}>
        Pay Bills
      </Text>
      <hr />

      <Grid
        templateColumns={["1fr", "repeat(2, 1fr)", "repeat(4, 1fr)"]}
        gap="12px"
        my="20px"
      >
        {VTU_FEATURE_MOCK_DATA.map((item) => (
          <VTUFeatureCard
            key={item.key}
            title={item.title}
            description={item.description}
            icon={item.icon}
            isDisabled={!vtuAvailable}
            onClick={() => handleOpenPayBillModal(item.key)}
          />
        ))}
      </Grid>

      <Box mt="18px" boxShadow={"lg"} borderRadius="10px" p="14px">
        <HStack justifyContent="space-between" mb="12px">
          <Text fontWeight="700" color="primary.500">
            Transactions History
          </Text>
        </HStack>

        <ReusableTable
          columns={columns}
          data={formattedTransactions}
          loading={transactionsLoading}
        />
      </Box>

      <PayBillModal
        isOpen={isPayBillModalOpen}
        onClose={closePayBillModal}
        defaultItem={selectedPayItem}
      />

      <ReusableModal
        isOpen={isTransactionModalOpen}
        onClose={closeTransactionModal}
        title="Transaction Details"
        size="md"
        footer={
          <Button size="sm" onClick={closeTransactionModal}>
            Close
          </Button>
        }
      >
        {selectedTransaction ? (
          <VStack align="stretch" spacing="10px" fontSize="14px" pb="6px">
            <HStack justifyContent="space-between">
              <Text color="gray.600">Transaction Ref</Text>
              <Text fontWeight="600">{selectedTransaction.transactionId}</Text>
            </HStack>
            <HStack justifyContent="space-between">
              <Text color="gray.600">Item Title</Text>
              <Text fontWeight="600">{selectedTransaction.itemTitle}</Text>
            </HStack>
            <HStack justifyContent="space-between">
              <Text color="gray.600">Phone/Decoder Number</Text>
              <Text fontWeight="600">
                {selectedTransaction.phoneOrDecoderNumber}
              </Text>
            </HStack>
            <HStack justifyContent="space-between">
              <Text color="gray.600">Amount</Text>
              <Text fontWeight="600">
                ₦{selectedTransaction.amount.toLocaleString()}
              </Text>
            </HStack>
            <HStack justifyContent="space-between">
              <Text color="gray.600">Status</Text>
              <Badge
                colorScheme={getStatusColor(selectedTransaction.status)}
                borderRadius="full"
                px="8px"
                py="2px"
                textTransform="none"
              >
                {selectedTransaction.status}
              </Badge>
            </HStack>
            <HStack justifyContent="space-between">
              <Text color="gray.600">Date</Text>
              <Text fontWeight="600">{selectedTransaction.date}</Text>
            </HStack>

            {/* Service-specific delivery details: token, certificate, PIN, etc. */}
            <VtuProviderDetails
              providerDetails={selectedTransaction.providerDetails}
            />
          </VStack>
        ) : null}
      </ReusableModal>
    </Box>
  );
};
